import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api, { apiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setHasAccess(data.has_access);
      return data.user;
    } catch {
      setUser(false);
      setHasAccess(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    setHasAccess(data.has_access);
    return data.user;
  };

  const register = async (form) => {
    const { data } = await api.post("/auth/register", form);
    setUser(data.user);
    setHasAccess(data.has_access);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
    setHasAccess(false);
  };

  const isAdmin = user && ["admin", "super_admin"].includes(user.role);
  const canRefer = user && (user.can_refer || ["admin", "super_admin"].includes(user.role));

  return (
    <AuthContext.Provider value={{ user, hasAccess, loading, login, register, logout, refresh, setUser, isAdmin, canRefer }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
export { apiError };
